import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressInvitesApiSiteInvitesApiUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'www._base_domain_': [
      {
        srcPath: '/_api/invites-web',
        destPath: '',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/invites-web',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/invites-web',
        destPath: '',
      },
      {
        srcPath: '/invites',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/invites-web',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/invites-web',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/invites-web',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/invites/invites/site',
        destPath: '/invites/site',
      },
      {
        srcPath: '/invites/invites/account',
        destPath: '/invites/account',
      },
      {
        srcPath: '/invites',
        destPath: '',
      },
      {
        srcPath: '/invites/site-invite',
        destPath: '/site-invite',
      },
      {
        srcPath: '/invites/account-invite',
        destPath: '/account-invite',
      },
    ],
    'dev._base_domain_': [
      {
        srcPath: '/_api/invites-web',
        destPath: '',
      },
    ],
    'platform.rise.ai': [
      {
        srcPath: '/invites/site',
        destPath: '/invites/site',
      },
      {
        srcPath: '/site-invite',
        destPath: '/site-invite',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_user-management_site-invites';

/**
 * Creates and sends emails inviting potential site contributors to become contributors in the requesting site.
 * > **Important**: This call requires an account level API key and cannot be authenticated with the standard authorization header. API keys are currently available to selected beta users only.
 */
export function bulkInvite(payload: object): RequestOptionsFactory<any> {
  function __bulkInvite({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.identity.invites.v1.site_invite',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.invites.api.SiteInvitesApi.BulkInvite',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressInvitesApiSiteInvitesApiUrl({
        protoPath: '/site-invite/bulk',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'invites.dateCreated' },
              { path: 'invites.expirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkInvite;
}

/**
 * Resends the email invitation to a potential site contributor.
 * > **Important**: This call requires an account level API key and cannot be authenticated with the standard authorization header. API keys are currently available to selected beta users only.
 */
export function resendInvite(payload: object): RequestOptionsFactory<any> {
  function __resendInvite({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.identity.invites.v1.site_invite',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.invites.api.SiteInvitesApi.ResendInvite',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressInvitesApiSiteInvitesApiUrl({
        protoPath: '/site-invite/{inviteId}/resend',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'invite.dateCreated' },
              { path: 'invite.expirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __resendInvite;
}

/**
 * Revokes a pending site contributor invite.
 * > **Important**: This call requires an account level API key and cannot be authenticated with the standard authorization header. API keys are currently available to selected beta users only.
 */
export function revokeInvite(payload: object): RequestOptionsFactory<any> {
  function __revokeInvite({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.identity.invites.v1.site_invite',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.invites.api.SiteInvitesApi.RevokeInvite',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressInvitesApiSiteInvitesApiUrl({
        protoPath: '/site-invite/{inviteId}/revoke',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __revokeInvite;
}
